<template>
  <v-container class="pa-0">
    <v-stepper :model-value="0">
      <!-- Header/Steps -->
      <v-stepper-header>
        <v-stepper-item
          complete
          :step="0"
          color="success"
        >
          <template #title>
            <div class="step-name-green">
              {{ t("Order Submitted") }}
            </div>
          </template>
        </v-stepper-item>
      </v-stepper-header>

      <v-divider class="mb-6" />

      <!-- Content/Tabs -->

      <div class="step-content">
        <v-container class="d-flex flex-row">
          <v-tabs
            v-model="activeTab"
            direction="vertical"
            class="vertical-slider-line v-col-2"
          >
            <v-tab class="tab-header">
              {{ t("Order info") }}
            </v-tab>
            <v-tab class="tab-header">
              {{ t("Order notifications title") }}
            </v-tab>
            <v-tab class="tab-header">
              {{ t("Share order title") }}
            </v-tab>
          </v-tabs>

          <v-window
            v-model="activeTab"
            class="tab-content-wrapper"
            continuous
            direction="vertical"
            height="auto"
          >
            <v-window-item
              :value="0"
              class="tab-content"
            >
              <div class="form-title">
                {{ t("Order info") }}
              </div>
              <div class="pa-2" />
              <div class="submitted-content">
                <div class="subtitle">
                  <v-icon
                    size="large"
                    color="success"
                  >
                    mdi-check-bold
                  </v-icon>
                  <span>{{ t("Your order was placed successfully") }}</span>
                </div>
                <div>{{ t("Your order ID") }}: {{ order.orderId }}</div>
                <OrderSubmittedUsForm
                  v-if="orderInfoPage === ORDER_INFO_PAGE_OPTIONS.us_form"
                />
                <OrderSubmittedNoSample
                  v-else-if="orderInfoPage === ORDER_INFO_PAGE_OPTIONS.no_sample"
                />
                <OrderSubmittedDefault
                  v-else
                />
              </div>

              <v-container class="tab-footer ml-auto">
                <Button
                  class="ml-sm-3"
                  @click="nextTab"
                >
                  {{ t("Next") }}
                  <v-icon
                    class="text-white icon-flipped"
                    end
                  >
                    mdi-keyboard-backspace
                  </v-icon>
                </Button>
              </v-container>
            </v-window-item>

            <v-window-item
              :value="1"
              class="tab-content"
            >
              <div class="form-title">
                {{ t("Order notifications title") }}
              </div>
              <OrderNotification
                :order-notifications="orderNotifications"
                :notifications="notifications"
                @save="orderNotificationSave"
                @removeAllNotifications="removeAllNotifications"
              />

              <v-container class="tab-footer ml-auto">
                <Button
                  class="ml-sm-3"
                  color="secondary"
                  @click="previousTab"
                >
                  {{ t("Back") }}
                </Button>
                <Button
                  class="ml-sm-3"
                  @click="nextTab"
                >
                  {{ t("Next") }}
                  <v-icon
                    class="text-white icon-flipped"
                    end
                  >
                    mdi-keyboard-backspace
                  </v-icon>
                </Button>
              </v-container>
            </v-window-item>

            <v-window-item
              :value="2"
              class="tab-content"
            >
              <ShareOrder
                v-model="showShareOrderDialog"
                :show-submit-order-share-dialog="showShareOrderDialog"
                :order-id="orderId"
                :screen-type="SHARE_ORDER_SCREEN_TYPE.order"
                @closeSubmitOrderShareDialog="closeSubmitOrderShareDialog"
                @hasSelectedUsers="hasSelectedUsers"
              />
              <v-container class="tab-footer">
                <Button
                  class="ml-sm-3"
                  color="secondary"
                  @click="previousTab"
                >
                  {{ t("Back") }}
                </Button>
                <Button
                  class="ml-sm-3 tab-footer--close"
                  @click="onFinishClick"
                >
                  {{ t("Finish") }}
                </Button>
              </v-container>
            </v-window-item>
          </v-window>
        </v-container>
      </div>
    </v-stepper>
  </v-container>
</template>


<script lang="ts" setup>
import { ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import { useOrdersStore } from "@/stores/orders";
import { usePatientsStore } from "@/stores/patients";
import { useOrderNotificationStore } from "@/stores/orderNotification";
import Button from "@/components/input-components/Button.vue";
import { SHARE_ORDER_SCREEN_TYPE, ORDER_INFO_PAGE_OPTIONS } from "@/utils/constants";
import type { Notification } from '@/entities/notification';
import OrderNotification from "@/components/orders/OrderNotification.vue";
import ShareOrder from "@/components/orders/ShareOrder.vue";
import OrderSubmittedDefault from '@/components/orders/OrderSubmittedDefault.vue';
import OrderSubmittedUsForm from '@/components/orders/OrderSubmittedUsForm.vue';
import OrderSubmittedNoSample from '@/components/orders/OrderSubmittedNoSample.vue';
import NotificationMessageService from "@/services/notificationmessage.service";
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const activeTab = ref(0);
const showShareOrderDialog = ref(false);
const usersSelected = ref(true);

const route = useRoute();
const ordersStore = useOrdersStore();
const patientsStore = usePatientsStore();
const orderNotificationStore = useOrderNotificationStore();

const orderId = computed(() => parseInt(route.params.orderId as string ?? "-1"));
const order = computed(() => ordersStore.getOrder);
const patient = computed(() => patientsStore.getPatient);
const orderNotifications = computed((): Notification[] => <Notification[]>ordersStore.getOrderNotifications);
const notifications = computed((): Notification[] => <Notification[]>orderNotificationStore.notifications);
const orderInfoPage = computed(() => ordersStore.getOrderInfoPage);

const emit = defineEmits(["close"]);

function nextTab() {
  activeTab.value++;
}

function previousTab() {
  activeTab.value--;
}

async function removeAllNotifications(notificationsToRemove: Notification[]) {
  for (const notification of notificationsToRemove) {
    const result = await ordersStore.deleteOrderNotification({
      orderId: orderId.value,
      notificationId: notification.notificationId,
    });
    if (result) {
      NotificationMessageService.showSuccess(t("Order notification settings deleted!"));
      await fetchOrderNotifications();
    }
  }
}

async function orderNotificationSave(
  payload: { remove?: Notification; add?: Notification },
  options: { silent?: boolean } = {}
) {
  try {
    if (payload.remove && Object.keys(payload.remove).length) {
      const result = await ordersStore.deleteOrderNotification({
        orderId: orderId.value,
        notificationId: payload.remove.notificationId,
      });
      if (result) {
        NotificationMessageService.showSuccess(t("Order notification settings deleted!"));
        await fetchOrderNotifications();
      }
    }

    if (payload.add && Object.keys(payload.add).length) {
      const result = await ordersStore.postOrderNotification({
        orderId: orderId.value,
        notification: payload.add,
      });
      if (result) {
        if (!options.silent) {
          NotificationMessageService.showSuccess(t("Order notification settings saved!"));
        }
        await fetchOrderNotifications();
      }
    }
  } catch (error) {
    if (!options.silent) {
      NotificationMessageService.showError(t("Failed to save order notifications."));
    } else {
      console.error(error);
    }
  }
}

async function fetchOrderNotifications() {
  await Promise.all([
    orderNotificationStore.fetchNotifications(),
    ordersStore.fetchOrderNotifications(orderId.value),
  ]);
}

function closeSubmitOrderShareDialog(args: string | null) {
  showShareOrderDialog.value = false;
  if (args === "finish") emit("close");
}

function onFinishClick() {
  if (usersSelected.value) {
    showShareOrderDialog.value = true;
  } else {
    emit("close");
  }
}

function hasSelectedUsers(selected: boolean) {
  usersSelected.value = selected;
}

onMounted(() => {
  fetchOrderNotifications();
})

</script>

<style lang="scss">
.tab-header {
  color: rgb(var(--v-theme-primary)) !important;
}

.submitted-content {
  text-align: left;
  margin: 0.625rem 0;
  max-width: 500px;

  &--bold {
    font-weight: bold;
  }

  > div:not(.inner-content),
  > .inner-content > div {
    text-align: left;
    margin: 0.9375rem 0;
  }

  .subtitle {
    text-transform: uppercase;
    font-weight: bold;
    font-size: 1.25rem;
    letter-spacing: 0.0625rem;
    margin-bottom: 1.25rem;
    color: rgb(var(--v-theme-green));
  }
}
</style>
